import { useState, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";
import { X, Search, FileText, Trash2, Upload } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/uiStore";
import { useSettingsStore } from "@/stores/settingsStore";
import type { CustomCsvSymbol } from "@/stores/settingsStore";
import { db } from "@/lib/lytick/db";
import { loadAndCommitCsvSymbol } from "@/hooks/useCsvImport";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

function formatGranularity(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

interface SymbolPickerOverlayProps {
  open: boolean;
}

export function SymbolPickerOverlay({ open }: SymbolPickerOverlayProps) {
  const { closeModal } = useUIStore();
  const { customCsvSymbols, lastActiveCsvHash, removeCustomCsvSymbol } = useSettingsStore(
    useShallow((state) => ({
      customCsvSymbols: state.customCsvSymbols,
      lastActiveCsvHash: state.lastActiveCsvHash,
      removeCustomCsvSymbol: state.removeCustomCsvSymbol,
    })),
  );

  const [query, setQuery] = useState("");

  const handleSelectCsv = useCallback(
    (meta: CustomCsvSymbol) => {
      closeModal();
      loadAndCommitCsvSymbol(meta).then((ok) => {
        if (!ok) {
          removeCustomCsvSymbol(meta.contentHash);
          useUIStore.getState().addToast(
            `${meta.symbol} data not found — please re-import the CSV`,
            "error",
          );
        }
      });
    },
    [closeModal, removeCustomCsvSymbol],
  );

  const handleRemoveCsv = useCallback(
    (contentHash: string) => {
      removeCustomCsvSymbol(contentHash);
      db.csvDatasets.delete(contentHash).catch(() => {});
    },
    [removeCustomCsvSymbol],
  );

  const handleLoadNew = useCallback(() => {
    closeModal();
    window.dispatchEvent(new CustomEvent("lytick:load-csv"));
  }, [closeModal]);

  const trimmed = query.trim().toLowerCase();
  const filtered = trimmed
    ? customCsvSymbols.filter((m) => m.symbol.toLowerCase().includes(trimmed))
    : customCsvSymbols;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) closeModal(); }}>
      <DialogContent
        showCloseButton={false}
        className="flex flex-col overflow-hidden gap-0 p-0 w-full max-w-md max-h-[70vh]"
        style={{ backgroundColor: "var(--bg-surface)" }}
      >
        {/* Header */}
        <div
          className="flex h-10 shrink-0 items-center justify-between px-4 border-b"
          style={{
            backgroundColor: "var(--bg-elevated)",
            borderColor: "var(--border-default)",
          }}
        >
          <DialogTitle
            className="text-sm font-semibold"
            style={{ color: "var(--text-primary)" }}
          >
            CSV Files
          </DialogTitle>
          <button
            type="button"
            onClick={closeModal}
            className="h-8 w-8 rounded-md interactive interactive-icon flex items-center justify-center"
            style={{ color: "var(--text-secondary)" }}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Search */}
        <div
          className="flex h-10 shrink-0 items-center gap-2 px-4 border-b"
          style={{ borderColor: "var(--border-default)" }}
        >
          <Search className="size-4 shrink-0 opacity-50" style={{ color: "var(--text-muted)" }} />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by symbol name…"
            autoFocus
            className="flex-1 h-9 min-w-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            style={{ color: "var(--text-primary)" }}
          />
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto overscroll-contain py-1 min-h-0">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center px-6">
              <div
                className="size-12 rounded-xl flex items-center justify-center mb-3"
                style={{ backgroundColor: "var(--bg-elevated)" }}
              >
                <FileText className="size-6 text-muted-foreground/30" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">
                {trimmed ? "No files match" : "No CSV files loaded"}
              </p>
              {!trimmed && (
                <p className="text-xs text-muted-foreground/50 mt-1.5">
                  Use the upload button in the toolbar to import a CSV
                </p>
              )}
            </div>
          ) : (
            filtered.map((meta) => (
              <CsvRow
                key={meta.contentHash}
                meta={meta}
                isActive={meta.contentHash === lastActiveCsvHash}
                onLoad={() => handleSelectCsv(meta)}
                onRemove={() => handleRemoveCsv(meta.contentHash)}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div
          className="h-12 shrink-0 flex items-center justify-end px-4 border-t"
          style={{
            backgroundColor: "var(--bg-elevated)",
            borderColor: "var(--border-default)",
          }}
        >
          <button
            type="button"
            onClick={handleLoadNew}
            className="h-8 px-3 rounded-md text-sm font-medium inline-flex items-center gap-2 interactive interactive-button"
            style={{
              backgroundColor: "var(--accent-primary)",
              color: "var(--bg-base)",
            }}
          >
            <Upload className="size-4" />
            Load new CSV
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CsvRow({
  meta,
  isActive,
  onLoad,
  onRemove,
}: {
  meta: CustomCsvSymbol;
  isActive: boolean;
  onLoad: () => void;
  onRemove: () => void;
}) {
  const dateRange = (() => {
    const fmt = (ts: number) => new Date(ts).toISOString().slice(0, 10);
    return `${fmt(meta.firstTs)} — ${fmt(meta.lastTs)}`;
  })();

  return (
    <div
      onClick={onLoad}
      className={cn(
        "flex items-center gap-3 px-3 py-2.5 mx-2 my-0.5 rounded-md min-h-11 cursor-pointer interactive",
        isActive ? "interactive-selected" : "",
      )}
      style={
        isActive
          ? {
              backgroundColor: "var(--surface-accent-hover)",
              border: "1px solid var(--accent-border)",
            }
          : { border: "1px solid transparent" }
      }
    >
      <FileText className="size-4 shrink-0" style={{ color: "var(--text-muted)" }} />
      <div className="flex-1 min-w-0">
        <div
          className="text-sm font-semibold truncate"
          style={{ color: "var(--text-primary)" }}
          title={meta.symbol}
        >
          {meta.symbol}
        </div>
        <p className="text-xs truncate mt-0.5" style={{ color: "var(--text-secondary)" }}>
          {formatGranularity(meta.granularitySeconds)} · {meta.rowCount.toLocaleString()} bars · {dateRange}
        </p>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="h-8 w-8 rounded-md interactive interactive-icon flex items-center justify-center shrink-0"
        style={{ color: "var(--text-muted)" }}
        aria-label="Remove CSV file"
      >
        <Trash2 className="size-4 hover:text-red-400 transition-colors" />
      </button>
    </div>
  );
}
